import { useMemo } from "react";

import { skipToken } from "@reduxjs/toolkit/query";
import {
    formatLeverage,
    getLeverageLoopDirection,
    LoopExpanded,
    useCurrentTime,
    useLoopHistoryQuery,
    useOracleHistoricalPrices
} from "@bridgesplit/abf-react";
import {
    BRAND_COLORS,
    Column,
    Dot,
    Icon,
    MEDIA,
    PriceLineChartWithAxis,
    Row,
    StatColumn,
    StatProps,
    Text,
    useAppPalette
} from "@bridgesplit/ui";
import { filterTruthy, formatDate, formatUsd, roundTimestamp } from "@bridgesplit/utils";
import { useMediaQuery } from "@mui/material";

import { useLoopContext } from "../../LoopContext";
import { useRatesComparisonPeriodSelect } from "../../../common";
import { useWindContext } from "../../wind";

export default function LoopPerformanceHistory() {
    const periodController = useRatesComparisonPeriodSelect("LoopDetailChartsPeriod");
    const data = useLoopPerformanceHistory(periodController.period);

    const isMobile = useMediaQuery(MEDIA.SM.below);

    const chartKeys = useGetPerformanceChartKeys();

    return (
        <PriceLineChartWithAxis
            keys={chartKeys}
            height={200}
            formatYAxis={formatUsd}
            formatXAxis={(seconds) => formatDate(seconds, "date", { showTime: "never" })}
            aspectRatio={isMobile ? 2 : 3}
            data={data}
            tooltip={(point) => Tooltip(point, data ? data[data.length - 1].date : undefined)}
        />
    );
}

function useGetPerformanceChartKeys() {
    const { secondary } = useAppPalette();

    return [
        { key: "leveragedValue" as const, color: secondary },
        { key: "price" as const, color: BRAND_COLORS.GRAY }
    ];
}

type PerformanceInfo = {
    date: number;
    price: number;
    leveragedValue: number;
};

function Tooltip(data: PerformanceInfo | undefined, startTime: number | undefined) {
    const { secondary } = useAppPalette();
    const { loopExpanded } = useLoopContext();
    const {
        form: { multiplier }
    } = useWindContext();

    if (!data || !loopExpanded) return null;

    const { date, leveragedValue, price } = data;
    const isShort = isShortExposure(loopExpanded);

    const leverage = formatLeverage(multiplier ?? loopExpanded.loopVault.maxLeverage ?? 1);

    const tokenSymbol = isShort ? loopExpanded.principalToken.symbol : loopExpanded.collateralToken.symbol;

    const actionVerb = isShort ? "Short" : "Long";
    const stats: StatProps[] = [
        {
            caption: `${leverage} ${actionVerb} Net Value`,
            value: (
                <Text variant="body2">
                    <Dot color={secondary} /> {formatUsd(leveragedValue)}
                </Text>
            ),
            captionVariant: "body2"
        },
        {
            caption: `${tokenSymbol} Price`,
            value: (
                <Text variant="body2">
                    <Dot color={BRAND_COLORS.GRAY} /> {formatUsd(price)}
                </Text>
            ),
            captionVariant: "body2"
        }
    ];

    return (
        <Column sx={{ minWidth: "240px" }} spacing={1.5}>
            <Text variant="body2" color="disabled">
                <Icon type="date" />
                <Row spacing={0.25}>
                    {formatDate(startTime, "date", { showTime: "never" })}
                    <Icon type="forward" /> {formatDate(date)}
                </Row>
            </Text>
            <StatColumn stats={leveragedValue < price ? stats.reverse() : stats} />
        </Column>
    );
}

function isShortExposure(loopExpanded: LoopExpanded | undefined): boolean {
    return loopExpanded ? getLeverageLoopDirection(loopExpanded) === "Short" : false;
}

function useLoopPerformanceHistory(period: number) {
    const { loopExpanded } = useLoopContext();
    const {
        form: { multiplier }
    } = useWindContext();
    const { now } = useCurrentTime(60000); // 1-minute refresh

    const collateralMint = loopExpanded?.collateralToken.mint;
    const principalMint = loopExpanded?.principalToken.mint;
    const leverage = multiplier ?? loopExpanded?.loopVault.maxLeverage ?? 1;
    const isShort = isShortExposure(loopExpanded);

    const { data: priceData } = useOracleHistoricalPrices({
        mints: [principalMint, collateralMint].filter(filterTruthy),
        startTimestamp: now - period
    });

    const { data: borrowRates } = useLoopHistoryQuery(
        loopExpanded
            ? {
                  timeLookback: period,
                  loopVaultIdentifier: loopExpanded.vaultIdentifier
              }
            : skipToken,
        { skip: !loopExpanded }
    );

    return useMemo(() => {
        if (
            !collateralMint ||
            !principalMint ||
            !priceData ||
            !borrowRates?.length ||
            !priceData[principalMint]?.length ||
            !priceData[collateralMint]?.length
        ) {
            return undefined;
        }

        const collateralPrices = priceData[collateralMint];
        const principalPrices = priceData[principalMint];

        const collateralMap = new Map(collateralPrices.map((p) => [roundTimestamp(p.startTimestamp), p.avgPrice]));
        const principalMap = new Map(principalPrices.map((p) => [roundTimestamp(p.startTimestamp), p.avgPrice]));
        const borrowRateMap = new Map(
            borrowRates.map(({ timestamp, borrowRate }) => [roundTimestamp(timestamp), borrowRate])
        );

        const initialDepositUsd = isShort ? principalPrices[0].avgPrice : collateralPrices[0].avgPrice;

        const initialPrincipal = ((leverage - 1) * initialDepositUsd) / principalPrices[0].avgPrice;
        const initialCollateral = (leverage * initialDepositUsd) / collateralPrices[0].avgPrice;

        const data: PerformanceInfo[] = [];
        const seen = new Set<number>();
        let lastTimestamp = roundTimestamp(now - period);
        let cumBorrowCost = 0;

        for (const { startTimestamp } of collateralPrices) {
            const ts = roundTimestamp(startTimestamp);
            if (seen.has(ts)) continue;
            seen.add(ts);

            const r = borrowRateMap.get(ts);
            const principalPrice = principalMap.get(ts);
            const collateralPrice = collateralMap.get(ts);
            if (!r || !principalPrice || !collateralPrice) continue;

            const dt = ts - lastTimestamp;
            cumBorrowCost += ((initialPrincipal * r) / 31556926) * dt;
            lastTimestamp = ts;

            const debtUsd = (initialPrincipal + cumBorrowCost) * principalPrice;
            const collateralUsd = initialCollateral * collateralPrice;
            const positionValue = collateralUsd - debtUsd;

            data.push({
                date: ts,
                price: isShort ? principalPrice : collateralPrice,
                leveragedValue: positionValue
            });
        }

        return data;
    }, [collateralMint, principalMint, priceData, borrowRates, isShort, leverage, now, period]);
}
